<script setup>
import axios from '@axios'
import { useUsersStore, useRestaurantsStore, usePreferredStatusStore } from "@/stores"
import { ref, computed, watch, onBeforeUnmount, onMounted } from "vue"
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import AgentRestaurantRow from '@core/components/AgentRestaurantRow.vue'
import RestaurantNotesDialog from '@core/components/RestaurantNotesDialog.vue'
import { noteShareStatus } from '@/utils/profileNotes'
import { mediaUrl } from '@core/utils/mediaUrl'
import ProfileContactLine from '@core/components/ProfileContactLine.vue'
import CmsText from '@/@core/components/CmsText.vue'
import { relativeAgo } from '@/utils/relativeAgo'
import {
  ALL_CITIES,
  FAVORITE_FILTERS,
  FAVORITE_SORTS,
  buildFavoritesView,
} from '@/utils/favoritesList'

// My Favorites (RM-296, Kapil 2026-09-17): the same plain list RM AI shows —
// name with heart + count, group, Preferred, who in your network favorited it,
// your note — with no thumbnail, no "+" and no view count. Four filters
// (All | Booked | Notes | Preferred), a Recent / A-Z sort, and the city
// selector; a row shows its city only under All Cities. The list rules
// themselves live in @/utils/favoritesList (unit-tested).
const props = defineProps({
  member: Object,
})

const { t } = useI18n()
const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

const restaurantsStore = useRestaurantsStore()
const preferredStatusStore = usePreferredStatusStore()
const { viewingDetailsRestaurant, restaurants, viewingDetailsRestaurantTab, favoriteNotes, unfavoritedNotes } = storeToRefs(restaurantsStore)

const isMyProfile = computed(() => myUser?.value?._id === props.member?._id)

// Notes can change server-side (RM AI saves them via its tools), so the view
// refreshes them on open instead of trusting the login-time copy.
onMounted(() => {
  restaurantsStore.refreshFavoriteNotes()
  // Restaurant notes on places the member did not favorite show under Notes too.
  if (isMyProfile.value) restaurantsStore.refreshUnfavoritedNotes().then(fetchFriendsData)
  loadMemberShares()
  loadMyShares()
})

/**
 * What THIS member chose to show, on the restaurants you are looking at
 * (Kapil, 2026-09-19): a note they shared reads as a note under their favorite,
 * and one they kept to themselves simply says "Private note".
 *
 * The server decides both. `shares` holds only what this viewer may read — a
 * Public Member Highlight, or a Network one when the two are mutually
 * connected — and `withPrivateNote` is restaurant IDS ONLY, never a word of
 * the note, and never a saved contact.
 */
const memberShares = ref({})
const memberPrivateNoteIds = ref([])

/**
 * My own approved Highlights, so a Favorites row can show what I chose to tell
 * others alongside what I privately remember (Kapil, 2026-09-21). Favorites
 * becomes useful memory — what I favorite, what I shared, what I kept — without
 * opening every restaurant.
 */
const myShares = ref({})

// Audiences per NOTE, so a restaurant with a shared older note and a private
// newer one says the truth about the note actually shown (Kapil, 2026-09-22).
const mySharesByEntry = ref({})

async function loadMyShares() {
  if (!isMyProfile.value) return
  try {
    const { data } = await axios.get('/profile/shares')
    const byRestaurant = {}
    const byEntry = {}
    for (const sh of data || []) {
      if (sh.status !== 'active' || !sh.restaurantId) continue
      const k = String(sh.restaurantId)
      if (!byRestaurant[k]) byRestaurant[k] = { text: sh.text, audiences: [] }
      byRestaurant[k].audiences.push(sh.audience)
      if (sh.entryId) (byEntry[sh.entryId] = byEntry[sh.entryId] || []).push(sh.audience)
    }
    myShares.value = byRestaurant
    mySharesByEntry.value = byEntry
  } catch {
    myShares.value = {}
    mySharesByEntry.value = {}
  }
}

/** The Highlight on one of MY favorites, if I made one. */
const myShareFor = id => myShares.value[String(id)] || null

/**
 * What a note of MINE is, in Kapil's words (2026-09-22): "Private Only" or
 * "Highlight shared with your Network". A share that went only to the
 * restaurant is not a Highlight anyone in my network sees, so it says where
 * it went instead of claiming either.
 */
const myNoteStatus = row => {
  // Per note. Only a row with no entry id (nothing should be missing one) falls
  // back to the restaurant as a whole — which can over-state sharing, never
  // wrongly claim "Private Only".
  const audiences = row.noteEntryId
    ? (mySharesByEntry.value[row.noteEntryId] || [])
    : (myShareFor(row.id)?.audiences || [])

  return noteShareStatus(audiences, row.name)
}

async function loadMemberShares() {
  if (isMyProfile.value || !props.member?._id) return
  try {
    const { data } = await axios.get(`/profile/member/${props.member._id}/shares`)
    memberShares.value = data?.shares || {}
    memberPrivateNoteIds.value = data?.withPrivateNote || []
  } catch {
    memberShares.value = {}
    memberPrivateNoteIds.value = []
  }
}

/** The newest thing they shared about this restaurant, if anything. */
const sharedTextFor = id => {
  const list = memberShares.value[String(id)] || []

  return list.length ? list[0].text : null
}

/** When they shared that newest highlight — shown under it. */
const sharedAtFor = id => {
  const list = memberShares.value[String(id)] || []

  return list.length ? list[0].at || null : null
}

/** They have something here and did not share it. Existence only. */
const hasPrivateNoteAt = id => !sharedTextFor(id)
  && memberPrivateNoteIds.value.includes(String(id))

/**
 * When they last shared a Highlight on each restaurant — what "Recent" can go
 * by on their profile (Kapil, 2026-09-23). A share owns its approved text, so
 * a changed Highlight is a fresh approval and a fresh time.
 */
const memberSharedAtById = computed(() => Object.fromEntries(
  Object.entries(memberShares.value || {}).map(([rid, list]) => [rid, (list && list[0] && list[0].at) || null]),
))

/**
 * The row's note and person blocks, newest first (Kapil, 2026-09-23). A person
 * the member wrote about IS their newest note, so that is one block. People
 * show on my own profile only.
 */
const rowBlocks = row => {
  const person = isMyProfile.value && !!row.noteContact
  const note = !!row.note && !(person && row.contactIsNote)
  if (person && note) return row.contactFirst ? ['person', 'note'] : ['note', 'person']
  if (person) return ['person']

  return note ? ['note'] : []
}

// One sentence, shown on tap. Deliberately says "notes" whatever the entry is.
const privateNoteHintOpen = ref(false)
const privateNoteHintName = ref('')

// "their", not "his" or "hers" — a name does not tell us someone's pronouns.
const privateNoteMessage = computed(() => `${props.member?.firstName || 'They'} has kept their notes `
  + `for ${privateNoteHintName.value || 'this restaurant'} private.`)

function openPrivateNoteHint(event) {
  const row = event?.currentTarget?.closest('.favorites-row')
  privateNoteHintName.value = row?.querySelector('.agent-restaurant-row__name')?.textContent?.trim() || ''
  privateNoteHintOpen.value = true
}

watch(() => props.member?._id, loadMemberShares)

// Whenever my notes are re-read — a change on the restaurant page, the notes
// pop-up or RM AI — re-read what I shared too, or a row keeps saying "Private
// Only" about a note I just shared until a reload (Kapil, 2026-09-23).
watch(favoriteNotes, () => { loadMyShares() })

/**
 * "The list is loaded" means the FULL list was fetched, not that the store
 * holds a restaurant or two (Kapil, 2026-09-22: Max's profile showed one row
 * and would not scroll). Opening a restaurant's page pushes that single
 * restaurant into the store (getSingleRestaurant), which used to read as
 * loaded — so a member's favorites shrank to whichever places the viewer had
 * happened to visit. The store already records the real answer.
 */
const restaurantsLoaded = ref(restaurantsStore.hasFetchedRestaurantList)

// Who in the member's network favorited each place — the row's relevance signal.
const fetchFriendsData = () => {
  if (!myUser.value || myUser.value.userLevel === 'dummy') return
  const favIds = props.member?.favoritedRestaurants || []

  // Rows under Notes include restaurants that are NOT favorites — they carry the same
  // "Favorited by" signal, so their ids belong in the same batch.
  const noteIds = (unfavoritedNotes.value || []).map(row => row.restaurantId)
  const ids = [...new Set([...favIds, ...noteIds].filter(Boolean))]
  if (ids.length) {
    restaurantsStore.fetchFriendsFavoritedBatch(ids)
  }
}

if (!restaurantsLoaded.value) {
  restaurantsStore.getRestaurants()
  const stopWatch = watch(() => restaurantsStore.hasFetchedRestaurantList, fetched => {
    if (fetched) {
      restaurantsLoaded.value = true
      fetchFriendsData()
      stopWatch()
    }
  })
} else {
  fetchFriendsData()
}

if (isMyProfile.value) {
  // Booking data + favoritedAt (not in /me, for faster app startup)
  usersStore.getMyFavoritesData()
  // Where the member holds Preferred status, shown on the row
  preferredStatusStore.loadPreferredStatus?.()
}

const route = useRoute()

// A city named in the link is used once the member's favorites show they have one there.
const selectedMarketFavoritesMy = ref(ALL_CITIES)
const linkedCity = typeof route.query.city === 'string' ? route.query.city : ''

// RM AI links the member straight to what it saved (/favorites?filter=Notes),
// and to the list it just answered from (/favorites?filter=Booked&sort=MostBooked&city=Chicago, IL).
const QUERY_FILTERS = ['All', 'Booked', 'Notes', 'Preferred']
const myFavoritesFilterSelection = ref(QUERY_FILTERS.includes(String(route.query.filter)) ? String(route.query.filter) : 'All')
const mySortSelection = ref(FAVORITE_SORTS.some(o => o.value === String(route.query.sort)) ? String(route.query.sort) : 'Recent')

// City filter bottom sheet state
const isCityFilterOpen = ref(false)
const citySearchQuery = ref('')
const pendingCitySelection = ref(ALL_CITIES)

// A connection's profile carries their rounded booking lines (favoritedRestaurantsBooked);
// the raw counts are only ever my own, or a platform admin's view.
const hasBookingData = computed(() => (
  props.member?.favoritedRestaurantsBooked?.length > 0
  || props.member?.favoritedRestaurantsBookingCount?.length > 0
))

// Own profile: the four. Another member's profile keeps what it had (their favorites
// that are also mine = "Shared"); a public profile with no booking data shows no chips.
const filterOptions = computed(() => {
  if (isMyProfile.value) return FAVORITE_FILTERS
  if (!hasBookingData.value) return []

  return [
    { value: 'All', text: 'All' },
    { value: 'Booked', text: 'Booked' },
    { value: 'Shared', text: 'Shared' },
    { value: 'Preferred', text: 'Preferred' },
  ]
})

// Sort labels are literal. They used to read through `discover.filters.az`,
// which the CMS overrides to "All" for the Discover chip — so the control
// rendered "Recent | All", which is not a sort (Kapil, 2026-09-18).
const sortOptions = computed(() => FAVORITE_SORTS.map(option => ({
  value: option.value,
  text: option.text,
})))

const sortLabel = computed(() => (
  sortOptions.value.find(o => o.value === mySortSelection.value)?.text || 'Recent'
))

// "This restaurant has marked you Preferred" — kept on the row after the Preferred Status filter went away.
const isPreferredMemberAt = id => isMyProfile.value && !!preferredStatusStore.isPreferredAt?.(id)

const favoritesView = computed(() => buildFavoritesView({
  restaurants: restaurants.value || [],
  member: props.member || {},
  // Another member's profile must never show MY notes against THEIR favorites.
  favoriteNotes: isMyProfile.value ? (favoriteNotes.value || {}) : {},
  unfavoritedNotes: isMyProfile.value ? (unfavoritedNotes.value || []) : [],
  friendsFavoritedByMap: restaurantsStore.friendsFavoritedByMap || {},
  filter: myFavoritesFilterSelection.value,
  sort: mySortSelection.value,
  city: selectedMarketFavoritesMy.value,
  markFavorited: isMyProfile.value,
  myFavoriteIds: myUser.value?.favoritedRestaurants || [],
  sharedAtById: isMyProfile.value ? {} : memberSharedAtById.value,
}))

const filteredRestaurantsMyFavorites = computed(() => favoritesView.value.rows)
const marketsOptionsFavoritesMy = computed(() => favoritesView.value.cityOptions)

// Once: after that the city is the member's to change. (The favorites may
// already be loaded when the page opens, so this can run straight away.)
if (linkedCity) {
  let applied = false
  watch(marketsOptionsFavoritesMy, options => {
    if (applied || !options.some(c => c.value === linkedCity)) return
    applied = true
    selectedMarketFavoritesMy.value = linkedCity
  }, { immediate: true })
}

const selectedCityDisplayText = computed(() => {
  const option = marketsOptionsFavoritesMy.value.find(c => c.value === selectedMarketFavoritesMy.value)

  return option ? option.text : 'All Cities'
})

// On the heading row the city sits beside the title and the sort, and
// "All Cities (440 Favorites)" ran off the side of a 375px screen. The count
// is kept for the city sheet, where there is room for it.
const selectedCityShortText = computed(() => selectedCityDisplayText.value.replace(/\s*\(.*\)\s*$/, ''))

const filteredCityOptions = computed(() => {
  if (!citySearchQuery.value) return marketsOptionsFavoritesMy.value
  const query = citySearchQuery.value.toLowerCase()

  return marketsOptionsFavoritesMy.value.filter(c => c.text.toLowerCase().includes(query))
})

const openCityFilter = () => {
  pendingCitySelection.value = selectedMarketFavoritesMy.value
  citySearchQuery.value = ''
  isCityFilterOpen.value = true
}

// Keep the member's filter — picking a city used to silently reset it.
const applyCityFilter = () => {
  selectedMarketFavoritesMy.value = pendingCitySelection.value
  isCityFilterOpen.value = false
}

// Infinite scroll pagination
const PAGE_SIZE = 20
const displayCount = ref(PAGE_SIZE)
const scrollSentinel = ref(null)
let observer = null

const displayedFavorites = computed(() => filteredRestaurantsMyFavorites.value.slice(0, displayCount.value))
const hasMore = computed(() => displayCount.value < filteredRestaurantsMyFavorites.value.length)

const loadMore = () => {
  if (hasMore.value) displayCount.value += PAGE_SIZE
}

watch([myFavoritesFilterSelection, selectedMarketFavoritesMy, mySortSelection], () => {
  displayCount.value = PAGE_SIZE
})

watch(scrollSentinel, (el, oldEl) => {
  if (observer && oldEl) observer.unobserve(oldEl)
  if (el) {
    if (!observer) {
      observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) loadMore()
      }, { rootMargin: '200px' })
    }
    observer.observe(el)
  }
})

onBeforeUnmount(() => {
  if (observer) observer.disconnect()
})

// Notes for a restaurant (Founder review, 2026-09-17): the row shows the newest note and
// "+N more notes"; tapping opens every dated entry in RestaurantNotesDialog — the SAME
// pop-up a restaurant's My Notes tab opens (Kapil, 2026-09-22), so the two cannot drift.
const isNoteDialogOpen = ref(false)

// The saved card a row's contact came from, opened in place.
const cardDialogImageId = ref(null)
const noteRestaurantId = ref('')
const noteRestaurantName = ref('')

const openNoteDialog = row => {
  noteRestaurantId.value = String(row.id)
  noteRestaurantName.value = row.name || ''
  isNoteDialogOpen.value = true
}

// The dialog already refreshed favorite notes; these are the parts only this list shows.
const onNotesChanged = () => {
  if (isMyProfile.value) {
    restaurantsStore.refreshUnfavoritedNotes()
    loadMyShares()
  }
}

// A restaurant the member told RM about before RM knew its city (capture-first never
// stops to ask). They can fill it in from the row; RM AI understands "Old Days is in Miami, FL" too.
const isCityPromptOpen = ref(false)
const cityPromptRestaurantId = ref(null)
const cityPromptRestaurantName = ref('')
const cityPromptValue = ref('')
const savingCity = ref(false)
const cityPromptError = ref('')

const openCityPrompt = row => {
  cityPromptRestaurantId.value = row.id
  cityPromptRestaurantName.value = row.name || ''
  cityPromptValue.value = ''
  cityPromptError.value = ''
  isCityPromptOpen.value = true
}

const saveCity = async () => {
  const market = (cityPromptValue.value || '').trim()
  if (!market) return
  savingCity.value = true
  cityPromptError.value = ''
  try {
    await axios.put(`/restaurant/${cityPromptRestaurantId.value}/city`, { market })
    await restaurantsStore.getRestaurants()
    if (isMyProfile.value) restaurantsStore.refreshUnfavoritedNotes()
    isCityPromptOpen.value = false
  } catch (error) {
    cityPromptError.value = error?.response?.data?.message || 'Could not save that city. Please try again.'
  } finally {
    savingCity.value = false
  }
}

const openRestaurant = row => {
  viewingDetailsRestaurantTab.value = 'Details'
  viewingDetailsRestaurant.value = row.restaurant
}
</script>

<template>
  <div>
    <!-- Loading State -->
    <div
      v-if="!restaurantsLoaded"
      class="d-flex justify-center align-center py-6"
    >
      <VProgressCircular
        indeterminate
        color="primary"
        size="32"
      />
    </div>

    <template v-else>
      <!--
        Heading row, the same shape as Discover (Kapil, 2026-09-18): title on
        the left, the controls inline on the right — sort first, then the city.
      -->
      <div class="mf-bar">
        <!--
          Someone else's list says whose it is, on the same line as the city
          (Kapil, 2026-09-22) — it opened on a lone "All Cities" before.
        -->
        <h2
          class="favorites-heading"
          :class="{ 'favorites-heading--member': !isMyProfile }"
        >
          {{ isMyProfile ? 'My Favorites' : `${member?.firstName || 'Their'}'s Favorites` }}
        </h2>

        <div class="mf-bar__controls">
          <span
            class="mf-inline"
            role="button"
            tabindex="0"
            @click="openCityFilter"
            @keydown.enter="openCityFilter"
          >
            {{ selectedCityShortText }}
            <VIcon
              icon="mdi-chevron-down"
              class="mf-inline__chevron"
            />
          </span>
        </div>
      </div>

      <!-- Filters: All | Booked | Notes | Preferred -->
      <div
        v-if="filterOptions.length > 0"
        class="filter-chips-bar"
      >
        <VChip
          v-for="filter in filterOptions"
          :key="filter.value"
          :color="myFavoritesFilterSelection === filter.value ? 'primary-dark' : undefined"
          :variant="myFavoritesFilterSelection === filter.value ? 'flat' : 'outlined'"
          class="filter-chip"
          :class="[{ 'filter-chip--active': myFavoritesFilterSelection === filter.value }]"
          size="small"
          @click="myFavoritesFilterSelection = filter.value"
        >
          <span>{{ filter.text }}</span>
        </VChip>

        <!--
          Sort sits with the filters, not with the city (Kapil, 2026-09-18):
          both control the list, while the city selector chooses what is in it.
          Plain text rather than a fifth pill, so it doesn't read as a filter.
        -->
        <VMenu>
          <template #activator="{ props: menuProps }">
            <span
              class="mf-sort"
              role="button"
              tabindex="0"
              v-bind="menuProps"
            >
              {{ sortLabel }}
              <VIcon
                icon="mdi-chevron-down"
                class="mf-sort__chevron"
              />
            </span>
          </template>
          <VList density="compact">
            <VListItem
              v-for="option in sortOptions"
              :key="option.value"
              :active="mySortSelection === option.value"
              @click="mySortSelection = option.value"
            >
              <VListItemTitle>{{ option.text }}</VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>
      </div>

      <div
        v-if="filteredRestaurantsMyFavorites.length == 0"
        class="text-xs text-center"
        style="line-height: 1em;"
      >
        <CmsText
          k="favorites.noFilterResults"
          tag="span"
        />
      </div>

      <div
        v-if="filteredRestaurantsMyFavorites.length > 0"
        class="favorites-list"
      >
        <div
          v-for="row in displayedFavorites"
          :key="row.id"
          class="favorites-row"
        >
          <!-- The shared identity row, with the member's note and booking context under it -->
          <AgentRestaurantRow
            :item="row.restaurant"
            :show-market="favoritesView.showMarket && !!row.market"
            hide-note
            wide
            full-size
          >
            <!--
              The restaurant's own facts, above the network line
              (Kapil, 2026-09-18: Booked sits above "Favorited by")
            -->
            <template #signals>
              <p
                v-if="isPreferredMemberAt(row.id)"
                class="favorites-row__preferred-member"
              >
                <VIcon
                  icon="mdi-star-check"
                  size="13"
                  color="primary-dark"
                />
                <span>You're a Preferred Member here</span>
              </p>
              <!-- The city we do not have yet — tappable, so the member can tell us -->
              <p
                v-if="favoritesView.showMarket && !row.market && isMyProfile"
                class="favorites-row__no-city"
                @click.stop="openCityPrompt(row)"
              >
                City not provided
              </p>
              <p
                v-if="row.bookedText"
                class="favorites-row__booked"
              >
                Booked {{ row.bookedText }}
              </p>
            </template>

            <template #meta>
              <!--
                The row shows the newest note and the newest saved person, in
                the order they happened (Kapil, 2026-09-23) — it used to put
                the note first always, so a card saved today sat under a note
                from two months ago. A person reads the way My Notes shows one:
                who they are (the heading), what the member said about them,
                the card, then its status — Kapil's order, the same as My
                Notes. Each block carries its own status line, so a note's
                "Highlight shared with your Network" never sits where it could
                read as the PERSON being shared — a contact never can be.
                People are on my own profile only.
              -->
              <template
                v-for="block in rowBlocks(row)"
                :key="block"
              >
                <ProfileContactLine
                  v-if="block === 'person'"
                  :contact="row.noteContact"
                  :card-image-id="row.noteCardImageId"
                  @open-card="cardDialogImageId = $event"
                >
                  <p
                    v-if="row.contactWords"
                    class="favorites-row__note favorites-row__note--under-person"
                    @click.stop="openNoteDialog(row)"
                  >
                    <VIcon
                      icon="mdi-note-text"
                      size="13"
                      color="primary-dark"
                    />
                    <span>{{ row.contactWords }}</span>
                  </p>
                  <template #after>
                    <p class="favorites-row__note-ago favorites-row__note-ago--after-card">
                      <span class="favorites-row__note-status">Contact · Private Only</span>
                      <span v-if="row.contactUpdatedAt"> · {{ relativeAgo(row.contactUpdatedAt) }} ago</span>
                    </p>
                  </template>
                </ProfileContactLine>
                <template v-else>
                  <!-- The newest note, when it was saved, and a way into the rest -->
                  <p
                    class="favorites-row__note"
                    @click.stop="openNoteDialog(row)"
                  >
                    <VIcon
                      icon="mdi-note-text"
                      size="13"
                      color="primary-dark"
                    />
                    <span>{{ row.note }}</span>
                  </p>
                  <!--
                    Underneath MY note: what it is, then when (Kapil, 2026-09-22).
                    The shared wording itself lives on the restaurant's Member
                    Highlights tab, so it is not repeated here.
                  -->
                  <p
                    v-if="isMyProfile || row.noteUpdatedAt"
                    class="favorites-row__note-ago"
                    @click.stop="openNoteDialog(row)"
                  >
                    <span
                      v-if="isMyProfile"
                      class="favorites-row__note-status"
                    >{{ myNoteStatus(row) }}</span>
                    <span v-if="isMyProfile && row.noteUpdatedAt"> · </span>
                    <span v-if="row.noteUpdatedAt">{{ relativeAgo(row.noteUpdatedAt) }} ago</span>
                  </p>
                </template>
              </template>
              <p
                v-if="row.noteMore > 0"
                class="favorites-row__more-notes"
                @click.stop="openNoteDialog(row)"
              >
                +{{ row.noteMore }} more {{ row.noteMore === 1 ? 'note' : 'notes' }}
              </p>
              <p
                v-if="!row.note && isMyProfile"
                class="favorites-row__add-note"
                @click.stop="openNoteDialog(row)"
              >
                <VIcon
                  icon="mdi-note-text-outline"
                  size="13"
                />
                <span>Add note</span>
              </p>
              <!--
                Someone else's profile: what they shared about this place reads
                as a note under it; a note they kept says only that it exists.
              -->
              <template v-if="!isMyProfile && sharedTextFor(row.id)">
                <p class="favorites-row__note">
                  <VIcon
                    icon="mdi-note-text"
                    size="13"
                    color="primary-dark"
                  />
                  <span>{{ sharedTextFor(row.id) }}</span>
                </p>
                <!--
                  When they shared it, as a highlight reads everywhere else
                  (Kapil, 2026-09-25: "yes add the time on member profiles").
                  A private note never shows a time: it says only that it exists.
                -->
                <p
                  v-if="sharedAtFor(row.id)"
                  class="favorites-row__note-ago favorites-row__note-ago--static"
                >
                  {{ relativeAgo(sharedAtFor(row.id)) }} ago
                </p>
              </template>
              <!--
                They have something here and kept it to themselves. A filled
                note icon, not the words (Kapil, 2026-09-21) — tapping it says
                so in plain language. The wording says "notes" whatever the
                entry actually is, so a saved contact is never singled out.
              -->
              <button
                v-else-if="!isMyProfile && hasPrivateNoteAt(row.id)"
                type="button"
                class="favorites-row__private-note"
                :aria-label="privateNoteMessage"
                @click.stop="openPrivateNoteHint"
              >
                <VIcon
                  icon="mdi-note-text"
                  size="13"
                  color="primary-dark"
                />
              </button>
            </template>
          </AgentRestaurantRow>
        </div>
        <div
          v-if="hasMore"
          ref="scrollSentinel"
          class="scroll-sentinel"
        />
      </div>
    </template>

    <VDialog
      v-model="privateNoteHintOpen"
      max-width="340"
    >
      <VCard class="pa-4">
        <p class="text-body-2 mb-4">
          {{ privateNoteMessage }}
        </p>
        <div class="d-flex justify-end">
          <VBtn
            variant="text"
            size="small"
            @click="privateNoteHintOpen = false"
          >
            Close
          </VBtn>
        </div>
      </VCard>
    </VDialog>

    <!--
      The saved card, full size, without leaving the list — the same pattern as
      the restaurant's My Notes and the chat bubble.
    -->
    <VDialog
      :model-value="!!cardDialogImageId"
      max-width="560"
      @update:model-value="cardDialogImageId = null"
    >
      <VCard class="favorites-card-dialog">
        <img
          v-if="cardDialogImageId"
          :src="mediaUrl(cardDialogImageId, 'original')"
          alt="Saved business card"
        >
        <div class="favorites-card-dialog__actions">
          <VBtn
            variant="text"
            size="small"
            @click="cardDialogImageId = null"
          >
            Close
          </VBtn>
        </div>
      </VCard>
    </VDialog>

    <!-- Notes for one restaurant: every dated entry, newest first. Adding writes a NEW entry. -->
    <RestaurantNotesDialog
      v-model="isNoteDialogOpen"
      :restaurant-id="noteRestaurantId"
      :restaurant-name="noteRestaurantName"
      @changed="onNotesChanged"
    />

    <!-- "City not provided": the member fills it in without leaving the list -->
    <VDialog
      v-model="isCityPromptOpen"
      max-width="420"
      @click:outside="isCityPromptOpen = false"
    >
      <VCard class="note-dialog-card">
        <div class="note-dialog-header">
          <VIcon
            icon="mdi-map-marker-outline"
            size="20"
            color="primary"
          />
          <span class="note-dialog-title">Which city is {{ cityPromptRestaurantName }} in?</span>
        </div>
        <VCardText class="note-dialog-body">
          <VTextField
            v-model="cityPromptValue"
            placeholder="Miami, FL"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :error-messages="cityPromptError"
            class="city-input"
            @keydown.enter="saveCity"
          />
        </VCardText>
        <VCardActions class="note-dialog-actions">
          <VSpacer />
          <VBtn
            variant="text"
            size="small"
            @click="isCityPromptOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            :disabled="!cityPromptValue.trim()"
            :loading="savingCity"
            @click="saveCity"
          >
            Save city
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- City Filter Bottom Sheet -->
    <VBottomSheet v-model="isCityFilterOpen">
      <VCard class="city-filter-sheet">
        <div class="sheet-handle" />
        <div class="sheet-header">
          <span class="sheet-title">Filter by City</span>
          <VBtn
            icon
            variant="text"
            size="small"
            @click="isCityFilterOpen = false"
          >
            <VIcon size="20">
              mdi-close
            </VIcon>
          </VBtn>
        </div>
        <VDivider />

        <!-- Search -->
        <div class="city-search-wrapper">
          <VTextField
            v-model="citySearchQuery"
            placeholder="Search cities..."
            variant="outlined"
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="city-search-input"
          />
        </div>

        <!-- City list -->
        <div class="city-list">
          <div
            v-for="city in filteredCityOptions"
            :key="city.value"
            class="city-list-item"
            @click="pendingCitySelection = city.value"
          >
            <span class="city-list-item-text">{{ city.text }}</span>
            <span
              class="city-radio"
              :class="{ 'city-radio--selected': pendingCitySelection === city.value }"
            />
          </div>
        </div>

        <!-- Done button -->
        <div class="city-done-wrapper">
          <VBtn
            block
            color="primary-dark"
            rounded="lg"
            size="large"
            @click="applyCityFilter"
          >
            Done
          </VBtn>
        </div>
      </VCard>
    </VBottomSheet>
  </div>
</template>

<style lang="scss" scoped>
/* My Favorites list — the Discover / Members' Favorites visual language applied to the
   approved My Favorites content (Founder review, 2026-09-17): hairline-separated rows,
   the same density and type scale, but no thumbnail, no "+", no chevron, no view count. */
/* Heading row: the same structure as Discover's — title left, controls right */
.mf-bar {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--ds-space-4);
  margin-top: var(--ds-space-3);
}

.mf-bar__controls {
  display: flex;
  align-items: baseline;
  gap: var(--ds-space-4);
  /* keeps the controls right whatever the heading's length */
  margin-left: auto;
  flex: 0 0 auto;
}

/* Sort sits in the filter row: a control, not a pill */
.mf-sort {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 6px;
  /* a real tap target on a phone without changing the line */
  min-height: 32px;
  font-family: var(--ds-font-body);
  font-size: 13.5px;
  font-weight: var(--ds-font-medium, 500);
  color: var(--ds-brand-ink);
  cursor: pointer;
  white-space: nowrap;
}

.mf-sort__chevron {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

/* On a phone "Most booked" is too long to share the chips' line, so the sort
   takes the next one: flush with the chips there, not indented (RM-396). */
@media (max-width: 480px) {
  .mf-sort {
    margin-left: 0;
  }
}

/* Sort and city read as one pair of inline controls, like Discover's city */
.mf-inline {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-md);
  font-weight: var(--ds-font-semibold);
  color: var(--ds-brand-ink);
  cursor: pointer;
  white-space: nowrap;
}

.mf-inline__chevron {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.favorites-heading {
  margin: 0;
  white-space: nowrap;
  /* "Christopher's Favorites" must not push the city off a phone screen */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: var(--ds-font-heading);
  font-size: var(--ds-text-xl);
  font-weight: var(--ds-font-medium, 500);
  /* no line-height override — Discover's title takes the inherited one, and
     the air under the heading has to match it (Kapil, 2026-09-18) */
  color: var(--ds-brand-ink, inherit);
  text-align: left;
}

/*
  Someone else's list is a section on their profile, not the page's own title,
  so it reads at the size of the city selector beside it — same size, same
  weight (Kapil, 2026-09-22: "way too big right now").
*/
.favorites-heading--member {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-md);
  font-weight: var(--ds-font-semibold);
}

@media (min-width: 960px) {
  .favorites-heading {
    font-size: var(--ds-text-3xl);
  }

  .favorites-heading--member {
    font-size: var(--ds-text-lg);
  }

  .mf-inline {
    font-size: var(--ds-text-lg);
  }
}

/* One column, full width of the page container (Kapil, 2026-09-18): the list
   used to sit in a 720px column centred inside a 1160px page, so on desktop it
   floated with a wide gutter either side while the notes wrapped early. */
.favorites-list {
  display: flex;
  flex-direction: column;
  gap: 0; /* the hairline separates rows, as on Discover */
  width: 100%;
  padding: 4px 0 0; /* the page supplies the side gutter; a second one insets the hairline */
}

/* Row: same rhythm as RestaurantGridCard's .card-wrapper-new */
.favorites-row {
  padding: 13px 2px;
  border-bottom: 1px solid var(--ds-border-subtle);
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

.favorites-row:last-of-type {
  border-bottom: none;
}

@media (min-width: 960px) {
  .favorites-row {
    padding-block: 18px;
  }
}

/* The metadata lines under the identity cluster — Discover's .content-subtitle scale */
.favorites-row__note,
.favorites-row__add-note,
.favorites-row__more-notes,
.favorites-row__preferred-member,
.favorites-row__no-city,
.favorites-row__booked {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 2px 0 0;
  font-family: var(--ds-font-body);
  font-size: 13.5px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--ds-text-body);
  white-space: normal;
  word-break: break-word;
  cursor: pointer;
  /* A tappable line needs a real target on a phone, without changing the visual rhythm */
  min-height: 24px;
  padding-block: 3px;
}

@media (min-width: 960px) {
  .favorites-row__note,
  .favorites-row__add-note,
  .favorites-row__more-notes,
  .favorites-row__preferred-member,
  .favorites-row__no-city,
  .favorites-row__booked {
    font-size: 14px;
  }
}

.favorites-row__note :deep(.v-icon),
.favorites-row__add-note :deep(.v-icon),
.favorites-row__preferred-member :deep(.v-icon) {
  margin-top: 2px;
  flex-shrink: 0;
}

.favorites-row__preferred-member {
  color: var(--ds-brand-primary-dark);
  cursor: default;
}

/* "+2 more notes" and "City not provided" read as the quiet, tappable affordances they are */
.favorites-row__more-notes,
.favorites-row__no-city {
  color: var(--ds-brand-primary-dark);
  padding-left: 17px; /* aligns under the note text, past the icon */
}

.favorites-row__no-city {
  padding-left: 0;
}

.favorites-row__private-note {
  /* The row is a flex column, which stretched this button to full width — and
     a button centers its content, so the icon floated mid-row. */
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 24px;
  min-height: 24px;
  background: none;
  border: 0;
  cursor: pointer;
  margin: 2px 0 0;
  /* The same filled green as the member's own note icon (Kapil, 2026-09-23).
     It was faded to grey, which read as "nothing here" or disabled; the tap
     is what says the notes are private. */
  padding: 0;
}

/* The member's words about a person, under the person's line. */
.favorites-row__note--under-person {
  margin-top: 4px;
}

/* The person's status closes the block, under the card. */
.favorites-row__note-ago--after-card {
  margin: 6px 0 0;
}

.city-input :deep(input) {
  font-size: 16px; /* iOS must not zoom the page on focus */
}

:deep(.v-btn--active) {
  border-bottom: 1px solid;
}

// Horizontal scrollable filter chips
.filter-chips-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--ds-space-2);
  /* the same air Discover puts between its heading row and its chips */
  padding-top: var(--ds-space-4);
  padding-bottom: 12px;
  justify-content: flex-start;
}

/* A second and third note sit under the first, aligned past the icon */
/* When the note was saved — the dialog's treatment, under the note it belongs to */
.favorites-row__note-ago {
  margin: 0 0 0 17px;
  font-family: var(--ds-font-body);
  font-size: 12px;
  line-height: 1.3;
  color: var(--ds-text-muted, #6B6B6B);
  cursor: pointer;
}

/* Someone else's highlight: its time opens nothing. */
.favorites-row__note-ago--static {
  cursor: default;
}

/* What the note IS outranks when it was written. */
.favorites-row__note-status {
  font-weight: 600;
  color: var(--ds-text-body);
}

/* Booked context carries a little more weight than the metadata around it,
   without competing with the restaurant's name (Kapil, 2026-09-18) */
.favorites-row__booked {
  font-weight: var(--ds-font-medium, 500);
  color: var(--ds-brand-ink, inherit);
}

/* Members write notes in lines ("dishes / drinks / who looked after us");
   collapsing them into one paragraph loses the shape they typed. Three lines
   is the cap on a row (Kapil, 2026-09-18) — a long note would otherwise make
   one row as tall as four others. The whole note is in the editor, a tap away. */
.favorites-row__note span {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}

// Text-only pills, matching the discovery tabs
.filter-chip {
  flex-shrink: 0;
  font-family: var(--ds-font-body) !important;
  font-size: var(--ds-text-xs) !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  border-color: var(--ds-border-default) !important;
  border-radius: 20px !important;
  transition: all var(--ds-duration-normal) ease !important;

  &:not(.filter-chip--active) {
    color: var(--ds-text-secondary) !important;

    &:hover {
      border-color: var(--ds-brand-primary-dark) !important;
      color: var(--ds-brand-primary-dark) !important;
    }
  }

  &.filter-chip--active {
    font-weight: 600 !important;
    box-shadow: 0 1px 4px rgba(63, 153, 143, 0.25);
  }
}





// City filter bottom sheet
.city-filter-sheet {
  border-radius: var(--ds-radius-lg) var(--ds-radius-lg) 0 0 !important;
  min-block-size: 70vh;
  max-block-size: 70vh;
  display: flex;
  flex-direction: column;
}

.sheet-handle {
  width: 36px;
  height: 4px;
  border-radius: var(--ds-radius-xs);
  background: rgba(var(--v-theme-on-surface), 0.2);
  margin: 10px auto 4px;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 8px 20px;
}

.sheet-title {
  font-size: var(--ds-text-md);
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.city-search-wrapper {
  padding: 12px 16px 8px;
}

.city-search-input {
  :deep(.v-field) {
    border-radius: var(--ds-radius-sm);
    font-size: 16px; // prevents iOS zoom
  }
}

.city-list {
  overflow-y: auto;
  flex: 1;
  padding: 0 4px;
}

.city-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }
}

.city-list-item-text {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-md);
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.city-radio {
  width: 22px;
  height: 22px;
  border-radius: var(--ds-radius-full);
  border: 2px solid rgba(var(--v-theme-on-surface), 0.3);
  flex-shrink: 0;
  transition: all var(--ds-duration-fast) ease;

  &--selected {
    border-color: var(--ds-brand-primary-dark);
    background: var(--ds-brand-primary-dark);
    box-shadow: inset 0 0 0 3px #fff;
  }
}

.city-done-wrapper {
  padding: 12px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.favorite-note-text {
  font-style: italic;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  line-height: 1.3;
}

.favorites-card-dialog {
  padding: 12px;
  border-radius: 12px;
}

.favorites-card-dialog img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.favorites-card-dialog__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

/* The city prompt's dialog. (The notes dialog moved to RestaurantNotesDialog.) */
.note-dialog-card {
  border-radius: var(--ds-radius-lg) !important;
  overflow: hidden;
}

.note-dialog-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  padding: var(--ds-space-5) var(--ds-space-6) var(--ds-space-2);
}

.note-dialog-title {
  font-size: var(--ds-text-md);
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.note-dialog-body {
  padding: 12px 24px 8px !important;
}

.note-dialog-actions {
  padding: 8px 20px 16px !important;
}

.scroll-sentinel {
  height: 1px;
  width: 100%;
}
</style>
